import { t } from '@ankr.com/common';
import { ChainID, ZETACHAIN_ATHENS3_CHAINS } from '@ankr.com/chains-list';

import { tendermintRestChains } from 'modules/endpoints/constants/groups';
import { renderNervosName, renderTacName } from 'uiKit/utils/metatags';
import {
  ALLORA_MAINNET_TENDERMINT_NAME,
  ALLORA_TESTNET_TENDERMINT_NAME,
  KAVA_TENDERMINT_NAME,
  KAVA_TESTNET_TENDERMINT_NAME,
  SECRET_TENDERMINT_NAME,
  SEI_TENDERMINT_NAME,
  TON_NAME,
  ZERO_G_TESTNET_TENDERMINT_NAME,
} from 'modules/chains/constants';

interface NestedItemBase {
  chainId: ChainID;
  label: string;
  shouldAvoidFormatting?: boolean;
}

const PART_TO_DELETE_FROM_STRING = '(testnet)';

const TENDERMINT_REST = `Tendermint REST`;

export const getCustomLabelForChainsCornerCases = ({
  chainId,
  label,
  shouldAvoidFormatting,
}: NestedItemBase) => {
  if (shouldAvoidFormatting) {
    return label;
  }

  if (ZETACHAIN_ATHENS3_CHAINS.includes(chainId)) {
    if (tendermintRestChains.includes(chainId)) {
      return `Athens 3 Zetachain Tendermint`;
    }

    return `Athens 3 ${label.replace(PART_TO_DELETE_FROM_STRING, '')}`;
  }

  if (tendermintRestChains.includes(chainId)) {
    if (label === TENDERMINT_REST) {
      return 'Tendermint';
    }

    if (chainId === ChainID.SEI_REST) {
      return SEI_TENDERMINT_NAME;
    }

    if (chainId === ChainID.SECRET_REST) {
      return SECRET_TENDERMINT_NAME;
    }

    if (chainId === ChainID.ALLORA_MAINNET_REST) {
      return ALLORA_MAINNET_TENDERMINT_NAME;
    }

    if (chainId === ChainID.ALLORA_TESTNET_REST) {
      return ALLORA_TESTNET_TENDERMINT_NAME;
    }

    if (chainId === ChainID.ZERO_G_GALILEO_TENDERMINT_REST) {
      return ZERO_G_TESTNET_TENDERMINT_NAME;
    }
  }

  if (chainId === ChainID.ZERO_G_GALILEO_TENDERMINT_REST) {
    return TENDERMINT_REST;
  }

  if (label === '0G Galileo Testnet RPC') {
    return '0G Galileo Testnet EVM JSON-RPC';
  }

  if (chainId === ChainID.KAVA_TENDERMINT_REST) {
    return KAVA_TENDERMINT_NAME;
  }

  if (chainId === ChainID.KAVA_TENDERMINT_REST_TESTNET) {
    return KAVA_TESTNET_TENDERMINT_NAME;
  }

  if (chainId === ChainID.NERVOS_GW || chainId === ChainID.NERVOS_CKB) {
    return renderNervosName(chainId);
  }

  if (chainId === ChainID.FUEL_REST) {
    return 'Fuel GraphQL';
  }

  if (chainId === ChainID.FUEL_SEPOLIA) {
    return 'Fuel Sepolia Testnet GraphQL';
  }

  if (label === 'TON REST') {
    return TON_NAME;
  }

  if (chainId === ChainID.ROLLUX_OPNODE) {
    return t('projects.new-project.step-2.mainnet-postfix', {
      label,
    });
  }

  if (chainId === ChainID.SUI_TESTNET) {
    return 'SUI Testnet';
  }

  if (chainId === ChainID.SUI_TESTNET_GRPC) {
    return 'SUI Testnet gRPC';
  }

  if (chainId === ChainID.ETHERLINK) {
    return 'Etherlink Mainnet';
  }

  if (chainId === ChainID.ZKVERIFY_MAINNET) {
    return 'JSON-RPC';
  }

  if (chainId === ChainID.ZKVERIFY_VFLOW_PARACHAIN_MAINNET) {
    return 'vFlow JSON-RPC';
  }

  if (chainId === ChainID.ZKVERIFY_VFLOW_PARACHAIN_TESTNET) {
    return 'vFlow Volta JSON-RPC';
  }

  if (chainId === ChainID.ZKVERIFY_VOLTA_TESTNET) {
    return 'Volta JSON-RPC';
  }

  if (chainId === ChainID.POLKADOT) {
    return 'Polkadot';
  }

  if (label.includes('TAC SPB') || label.includes('TAC Tendermint')) {
    return renderTacName(chainId);
  }

  return label;
};
